<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_ACCOUNT_BALANCE_MOVEMENT_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            text="Export"
            variant="primary"
            :disabled="isDisabled"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              CSV
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              PDF
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              label="Filter"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              label="Reset Filter"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="accountBalanceTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="tableHeaders"
      primary-key="accountNumber"
      show-empty
      empty-text="No records available"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(openingBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(grossInflows)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(grossOutflows)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

      <template #cell(closingBalance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>
      <template #cell(lastActivityDate)="data">
        <span class="text-nowrap">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      ok-title="Apply"
      cancel-title="Cancel"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      title="Filters"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="accountBalanceMovementRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                label="Account Name"
                placeholder="Enter Account Name"
                type="text"
                for="account-name"
              />
            </b-col>
            <!-- Account Name -->
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.accountNumber"
                label="Account Number"
                placeholder="Enter Account Number"
                type="text"
                for="account-number"
              />
            </b-col>
          <!-- Account Number -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                label="Financial Date (From)"
                rules="required"
                error-name="Financial Date From"
                :config="datePickerConfig"
                placeholder="Date From"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                rules="required"
                :min-date="new Date()"
                label="Financial Date (To)"
                error-name="Financial Date To"
                :config="datePickerConfig"
                placeholder="Date To"
              />
            </b-col>
          <!-- End Date -->
          </b-row>

          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                label="Product Name"
                placeholder="Select"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                label="Branch"
                placeholder="Select"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
          <!-- Branch -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { tableMixins } from '@core/mixins/table'
import { modalMixins } from '@core/mixins/modal'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import Ripple from 'vue-ripple-directive'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import XLSX from 'xlsx'
import { heightFade } from '@core/directives/animations'
import { toBalance, formatAmount, toFormatAmount } from '@/helpers/formatAmount'
import toNumber from '@/helpers/toNumber'
import { toCustomDate, toDateWithYearFirst } from '@/helpers/formatDate'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    InputField,
    SelectField,
    BModal,
    BForm,
    BDropdown,
    BDropdownItem,
    TableEntries,
    DatePickerField,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      formatAmount,
      toBalance,
      toFormatAmount,
      toDateWithYearFirst,
      query: '',
      currentDate: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: 'Save',
      isEditable: false,
      isBusy: false,
      isDisabled: true,
      required,
      modalTitle: 'Account Balance Movement Report',
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'branchName', sortable: true },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'openingBalance', sortable: true, label: 'Beginning Balance' },
        { key: 'grossInflows', sortable: true, label: 'Gross Inflows' },
        { key: 'grossOutflows', sortable: true, label: 'Gross Outflows' },
        { key: 'closingBalance', sortable: true, label: 'Ending Balance' },
        { key: 'lastActivityDate', sortable: true, label: 'Last Activity Date' },
      ],
      filter: {
        accountNumber: '',
        accountName: '',
        financialDateFrom: null,
        financialDateTo: null,
        productCode: '',
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      productOptions: [],
      branchOptions: [],
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
      currentFinancialDate: 'operations/getCurrentFinancialDate',
    }),
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Account Name': item.accountName,
        'Account Number': item.accountNumber,
        Product: item.productName,
        Branch: item.branchName,
        'Account Officer': item.accountOfficer,
        'Beginning Balance': item.openingBalance ? formatAmount(item.openingBalance) : 0,
        'Gross Inflows': item.grossInflows ? formatAmount(item.grossInflows) : 0,
        'Gross Outflows': item.grossOutflows ? formatAmount(item.grossOutflows) : 0,
        'Ending Balance': item.closingBalance ? formatAmount(item.closingBalance) : 0,
        'Last Activity Date': item.lastActivityDate ? this.formatCustomDate(item.lastActivityDate) : '',
      }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchBalanceMovementReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchBalanceMovementReport()
    },
  },
  mounted() {
    this.fetchBalanceMovementReport()
    this.fetchBranches()
    this.fetchProduct()
  },
  methods: {
    ...mapActions({
      getProduct: 'account/getProducts',
      getBranch: 'operations/getBranchList',
      getBranchByCode: 'operations/getBranchByCode',
      getAccountBalanceMovement: 'reports/getAccountBalanceMovement',
    }),
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.financialDateFrom = null
      this.filter.financialDateTo = null
      this.fetchBalanceMovementReport()
    },
    async fetchBranches() {
      try {
        const response = await this.getBranch({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.branchOptions = data.data
            .filter(dataItem => dataItem.status === 'Active')
            .map(item => ({
              label: item.name,
              value: item.code.toString(),
            }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchProduct() {
      try {
        const response = await this.getProduct({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.ACCOUNT_BALANCE_MOVEMENT_REPORT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active' && dataItem.type !== 'Loan').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.accountBalanceMovementRules.validate().then(async success => {
        if (success) {
          this.fetchBalanceMovementReport()
        }
      })
    },
    async fetchBalanceMovementReport() {
      const dt = new Date(this.currentFinancialDate.financialDate)
      const year = dt.getFullYear()
      const month = dt.getMonth()
      const firstDayMonth = new Date(year, month, 1)
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        if (this.filter.financialDateFrom === null && this.filter.financialDateTo === null) {
          this.currentDate = {
            financialDateFrom: toDateWithYearFirst(firstDayMonth),
            financialDateTo: toDateWithYearFirst(this.currentFinancialDate.financialDate),
          }
        }
        const response = await this.getAccountBalanceMovement({
          search: this.filter.financialDateFrom !== null ? this.filter : this.currentDate,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.reportData = data.data.map(item => ({
            ...item,
            closingBalance: Number(item.closingBalance),
            grossInflows: Number(item.grossInflows),
            grossOutflows: Number(item.grossOutflows),
            openingBalance: Number(item.openingBalance),
          }))
          this.totalCount = data.recordCount
          this.isBusy = false
          this.isDisabled = false
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getAccountBalanceMovement({
          search: this.filter.financialDateFrom !== null ? this.filter : this.currentDate,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data.map(item => ({
            ...item,
            closingBalance: Number(item.closingBalance),
            grossInflows: Number(item.grossInflows),
            grossOutflows: Number(item.grossOutflows),
            openingBalance: Number(item.openingBalance),
          }))
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Account Balance Movement Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Account_Balance_Movement_Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getAccountBalanceMovement({
          search: this.filter.financialDateFrom !== null ? this.filter : this.currentDate,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data.map(item => ({
            ...item,
            closingBalance: Number(item.closingBalance),
            grossInflows: Number(item.grossInflows),
            grossOutflows: Number(item.grossOutflows),
            openingBalance: Number(item.openingBalance),
          }))
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Account_Balance_Movement_Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Account Balance Movement Report'],
            [`${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]
          const Data = this.allSearchedData.map((item, index) => ({
            'S/N': index + 1,
            'Account Name': item.accountName,
            'Account Number': item.accountNumber,
            Product: item.productName,
            Branch: item.branchName,
            'Account Officer': item.accountOfficer,
            'Beginning Balance': item.openingBalance ? toFormatAmount(item.openingBalance) : 0,
            'Gross Inflows': item.grossInflows ? toFormatAmount(item.grossInflows) : 0,
            'Gross Outflows': item.grossOutflows ? toFormatAmount(item.grossOutflows) : 0,
            'Ending Balance': item.closingBalance ? toFormatAmount(item.closingBalance) : 0,
            'Last Activity Date': item.lastActivityDate ? this.formatCustomDate(item.lastActivityDate) : '',
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Account Balance Movement Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async pdfExport() {
      try {
        this.isBusy = true
        const response = await this.getAccountBalanceMovement({
          search: this.filter.financialDateFrom !== null ? this.filter : this.currentDate,
          page: 1,
          limit: 100000,
        })
        const { status, data } = response
        if (status === 200) {
          this.isBusy = false
          this.isDisabled = false
          const pdfData = data.data.map(item => ({
            ...item,
            openingBalance: item.openingBalance ? formatAmount(item.openingBalance) : 0,
            grossInflows: item.grossInflows ? formatAmount(item.grossInflows) : 0,
            grossOutflows: item.grossOutflows ? formatAmount(item.grossOutflows) : 0,
            closingBalance: item.closingBalance ? formatAmount(item.closingBalance) : 0,
            lastActivityDate: item.lastActivityDate ? this.formatCustomDate(item.lastActivityDate) : '',
          }))

          const columns = [
            { title: 'Acct Name', key: 'accountName' },
            { title: 'Acct Number', key: 'accountNumber' },
            { title: 'Product', key: 'productName' },
            { title: 'Branch', key: 'branchName' },
            { title: 'Acct Officer', key: 'accountOfficer' },
            { title: 'Beginning Bal.', key: 'openingBalance' },
            { title: 'Gross Inflows', key: 'grossInflows' },
            { title: 'Gross Outflows', key: 'grossOutflows' },
            { title: 'Ending Bal.', key: 'closingBalance' },
            { title: 'Last Activity Date', key: 'lastActivityDate' },
          ]

          const doc = jsPDF('landscape', 'pt', 'a4')
          const logo = this.reportInfo.map(item => item.orgLogo)
          const orgName = this.userDetails.institutionName.toUpperCase()
          const orgLogo = `${logo}`
          doc.addImage(orgLogo, 'JPEG', 40, 30)
          doc.setFontSize(20)
          doc.text(180, 60, `${orgName}`)
          doc.setFontSize(15)
          doc.text(180, 80, 'Account Balance Movement Report')
          doc.setFontSize(12)
          doc.text(180, 95, `${(this.filter.financialDateFrom !== null && this.filter.financialDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.financialDateFrom)} - ${this.formatCustomDate(this.filter.financialDateTo)}` : 'Date Range: All'}`)
          doc.text(180, 110, `${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`)
          doc.autoTable({
            theme: 'striped', // grid, plain
            columns,
            body: pdfData,
            startY: 150,
            // split overflowing columns into pages
            horizontalPageBreak: true,
            // repeat this column in split pages
            horizontalPageBreakRepeat: 'id',
            tableWidth: 'auto', // 'auto', 'wrap' or a number,
            margin: {
              horizontal: 20,
              vertical: 20,
            },
            bodyStyles: {
              valign: 'top',
            },
            styles: {
              overflow: 'linebreak',
              cellWidth: 'wrap',
              maxCellHeight: 4,
              fontSize: 8,
              cellPadding: 5,
            },
            columnStyles: {
              text: {
                cellWidth: 'wrap',
              },
            },
          })
          // Footer page number
          const pages = doc.internal.getNumberOfPages()
          const pageWidth = doc.internal.pageSize.width
          const pageHeight = doc.internal.pageSize.height
          doc.setFontSize(5)

          for (let j = 1; j < pages + 1; j += 1) {
            const horizontalPos = pageWidth / 2 // Can be fixed number
            const verticalPos = pageHeight - 10 // Can be fixed number
            doc.setPage(j)
            doc.text(`page ${j} of ${pages}`, horizontalPos, verticalPos, { align: 'center' }) // Optional text styling});
          }
          doc.save('Account-Balance-Movement-Report.pdf')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    formatNumber: toNumber,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
